import { mqttLogger } from '@hooks/mqtt/utils';
import { generateMqttClient } from '@pages/_loader/initMqttClient.loader';
import { type Getter, type Setter, type WritableAtom } from 'jotai';
import { MqttClient } from 'mqtt';
import { MqttConnectionStatus, type MqttMetadata } from '../connection/types';

export enum ConnectionEvent {
  Connecting = 'connecting',
  Connected = 'connected',
  Disconnected = 'disconnected',
  Reconnecting = 'reconnecting',
  Error = 'error',
  Initialized = 'initialized',
}

export const MQTT_CONNECTION_TIMEOUT = 30000;
export const MQTT_CONNECTION_CHECK_INTERVAL = 500;

export const clientHandlersMap = new WeakMap<MqttClient, boolean>();

export function convertToError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

export async function waitForCondition<T>(
  checkCondition: () => {
    value: T;
    isComplete: boolean;
    error?: Error;
  },
  options: {
    interval?: number;
    timeout?: number;
    timeoutMessage?: string;
  } = {},
): Promise<T> {
  const {
    interval = MQTT_CONNECTION_CHECK_INTERVAL,
    timeout = MQTT_CONNECTION_TIMEOUT,
    timeoutMessage = 'Operation timeout',
  } = options;

  return new Promise<T>((resolve, reject) => {
    let elapsed = 0;

    const checkInterval = setInterval(() => {
      elapsed += interval;

      try {
        const result = checkCondition();

        if (result.error) {
          clearInterval(checkInterval);
          reject(result.error);
        } else if (result.isComplete) {
          clearInterval(checkInterval);
          resolve(result.value);
        } else if (elapsed >= timeout) {
          clearInterval(checkInterval);
          reject(new Error(`${timeoutMessage} after ${timeout}ms`));
        }
      } catch (error) {
        clearInterval(checkInterval);
        reject(error);
      }
    }, interval);
  });
}

export function logConnectionEvent(
  event: ConnectionEvent,
  details?: {
    error?: unknown;
    timestamp?: number;
    message?: string;
  },
): void {
  const message = details?.message || `MQTT ${event}`;

  switch (event) {
    case ConnectionEvent.Error:
      mqttLogger.error(message, details?.error);
      break;
    case ConnectionEvent.Connected:
    case ConnectionEvent.Initialized:
      mqttLogger.info(
        message,
        details?.timestamp
          ? `at ${new Date(details.timestamp).toISOString()}`
          : '',
      );
      break;
    case ConnectionEvent.Disconnected:
      mqttLogger.warn(message);
      break;
    default:
      mqttLogger.debug(message);
  }
}

export function getConnectionStatus(
  client: MqttClient | null,
  metadata: MqttMetadata,
): MqttConnectionStatus {
  if (client?.connected) {
    return MqttConnectionStatus.CONNECTED;
  }

  if (client?.reconnecting) {
    return MqttConnectionStatus.RECONNECTING;
  }

  if (metadata.isInitializing) {
    return MqttConnectionStatus.CONNECTING;
  }

  if (metadata.error && !client?.connected) {
    return MqttConnectionStatus.ERROR;
  }

  return MqttConnectionStatus.DISCONNECTED;
}

export async function connectToMqtt(
  get: Getter,
  set: Setter,
  mqttClientAtom: WritableAtom<MqttClient | null, [MqttClient | null], void>,
  mqttMetadataAtom: WritableAtom<
    MqttMetadata,
    [MqttMetadata | ((prev: MqttMetadata) => MqttMetadata)],
    void
  >,
): Promise<MqttClient> {
  const existingClient = get(mqttClientAtom);
  const metadata = get(mqttMetadataAtom);

  if (existingClient?.connected) {
    mqttLogger.info('MQTT client already connected');
    return existingClient;
  }

  if (metadata.connectionPromise) {
    mqttLogger.info(
      'MQTT connection already in progress, awaiting existing promise',
    );
    return metadata.connectionPromise;
  }

  const connectionPromise = createMqttConnection(
    existingClient,
    get,
    set,
    mqttClientAtom,
    mqttMetadataAtom,
  );

  set(mqttMetadataAtom, {
    ...metadata,
    isInitializing: true,
    error: undefined,
    connectionPromise,
  });

  return connectionPromise;
}

export async function createMqttConnection(
  existingClient: MqttClient | null,
  _get: Getter,
  set: Setter,
  mqttClientAtom: WritableAtom<MqttClient | null, [MqttClient | null], void>,
  mqttMetadataAtom: WritableAtom<
    MqttMetadata,
    [MqttMetadata | ((prev: MqttMetadata) => MqttMetadata)],
    void
  >,
): Promise<MqttClient> {
  logConnectionEvent(ConnectionEvent.Connecting);

  try {
    let client: MqttClient;

    if (existingClient) {
      mqttLogger.info('Reusing existing MQTT client instance');
      client = existingClient;

      if (!client.connected && !client.reconnecting) {
        client.connect();
      } else if (client.reconnecting) {
        mqttLogger.info('Client is reconnecting, waiting for completion');
      }

      await waitForCondition<void>(
        () => ({
          value: undefined,
          isComplete: client.connected,
          error: undefined,
        }),
        {
          interval: MQTT_CONNECTION_CHECK_INTERVAL,
          timeout: MQTT_CONNECTION_TIMEOUT,
          timeoutMessage: 'MQTT connection timeout',
        },
      );
    } else {
      mqttLogger.info('Creating new MQTT client instance');
      client = await generateMqttClient();

      set(mqttClientAtom, client);
      setupEventHandlers(client, set, mqttMetadataAtom);
    }

    set(mqttMetadataAtom, {
      isInitializing: false,
      lastConnectedAt: Date.now(),
      connectionPromise: undefined,
    });

    logConnectionEvent(ConnectionEvent.Connected, { timestamp: Date.now() });
    return client;
  } catch (error) {
    const errorObj = convertToError(error);

    if (!existingClient) {
      set(mqttClientAtom, null);
    }

    set(mqttMetadataAtom, {
      isInitializing: false,
      error: errorObj,
      connectionPromise: undefined,
    });

    logConnectionEvent(ConnectionEvent.Error, { error });
    throw errorObj;
  }
}

export async function disconnectFromMqtt(
  get: Getter,
  set: Setter,
  mqttClientAtom: WritableAtom<MqttClient | null, [MqttClient | null], void>,
  mqttMetadataAtom: WritableAtom<
    MqttMetadata,
    [MqttMetadata | ((prev: MqttMetadata) => MqttMetadata)],
    void
  >,
  force = false,
): Promise<void> {
  const client = get(mqttClientAtom);

  if (!client) {
    mqttLogger.info('No MQTT client to disconnect');
    return;
  }

  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      mqttLogger.warn('Disconnect timeout - forcing cleanup');
      cleanupEventHandlers(client);
      set(mqttClientAtom, null);
      set(mqttMetadataAtom, {});
      resolve();
    }, 5000);

    client.end(force, {}, (error?: Error) => {
      clearTimeout(timeoutId);

      if (error) {
        mqttLogger.error('Error during disconnect', error);
      }

      cleanupEventHandlers(client);

      set(mqttClientAtom, null);
      set(mqttMetadataAtom, {});
      logConnectionEvent(ConnectionEvent.Disconnected);
      resolve();
    });
  });
}

export function setupEventHandlers(
  client: MqttClient,
  set: Setter,
  mqttMetadataAtom: WritableAtom<
    MqttMetadata,
    [MqttMetadata | ((prev: MqttMetadata) => MqttMetadata)],
    void
  >,
): void {
  if (clientHandlersMap.has(client)) {
    mqttLogger.debug('Event handlers already attached to this client');
    return;
  }

  const connectHandler = () => {
    logConnectionEvent(ConnectionEvent.Connected);
    set(mqttMetadataAtom, (prev: MqttMetadata) => ({
      ...prev,
      lastConnectedAt: Date.now(),
      error: undefined,
      isInitializing: false,
      connectionPromise: undefined,
    }));
  };

  const closeHandler = () => {
    logConnectionEvent(ConnectionEvent.Disconnected);
  };

  const reconnectHandler = () => {
    logConnectionEvent(ConnectionEvent.Reconnecting);
  };

  const offlineHandler = () => {
    mqttLogger.warn('MQTT client offline');
  };

  const errorHandler = (error: Error) => {
    logConnectionEvent(ConnectionEvent.Error, { error });
    set(mqttMetadataAtom, (prev: MqttMetadata) => ({
      ...prev,
      error,
    }));
  };

  client.on('connect', connectHandler);
  client.on('close', closeHandler);
  client.on('reconnect', reconnectHandler);
  client.on('offline', offlineHandler);
  client.on('error', errorHandler);

  clientHandlersMap.set(client, true);
}

export function cleanupEventHandlers(client: MqttClient): void {
  if (!clientHandlersMap.has(client)) {
    return;
  }

  client.removeAllListeners('connect');
  client.removeAllListeners('close');
  client.removeAllListeners('reconnect');
  client.removeAllListeners('offline');
  client.removeAllListeners('error');

  clientHandlersMap.delete(client);
}
